import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Straighten as LineIcon,
  Timeline as PolylineIcon,
  Clear as ClearIcon,
  Crop as AreaIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import Draggable from 'react-draggable';
import { useMapboxDraw } from './useMapboxDraw';

interface MeasureWidgetProps {
  mode: 'line' | 'polyline' | 'area';
  onModeChange: (mode: 'line' | 'polyline' | 'area') => void;
  measureResults: Array<{ distance?: number; area?: number; coordinates: number[][] }>;
  onClear: () => void;
  onClose: () => void;
  isDrawing: boolean;
  distanceHasStarted: boolean;
  liveMeasurement?: number | null;
}

const MeasureWidget: React.FC<MeasureWidgetProps> = ({
  mode,
  onModeChange,
  measureResults,
  onClear,
  onClose,
  isDrawing,
  distanceHasStarted,
  liveMeasurement
}) => {
  const dragHandleRef = useRef(null);
  const [useMetric, setUseMetric] = useState(true); // true for km/km², false for m/m²

  const handleModeChange = (newMode: 'line' | 'polyline' | 'area') => {
    onModeChange(newMode);
  };

  const handleClear = () => {
    onClear();
  };

  const handleUnitToggle = () => {
    setUseMetric(!useMetric);
  };

  const getModeIcon = (modeType: 'line' | 'polyline' | 'area') => {
    switch (modeType) {
      case 'line':
        return <LineIcon fontSize="small" />;
      case 'polyline':
        return <LineIcon fontSize="small" />;
      case 'area':
        return <AreaIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatMeasurement = (value: number, isArea: boolean = false) => {
    let displayValue = value;
    let unitLabel = isArea ? 'km²' : 'km';
    
    if (!useMetric) {
      displayValue = isArea ? value * 1000000 : value * 1000;
      unitLabel = isArea ? 'm²' : 'm';
    }
    
    return `${displayValue.toFixed(2)} ${unitLabel}`;
  };

  const getCurrentMeasurement = () => {
    // Show live measurement when actively drawing
    if (isDrawing && distanceHasStarted && liveMeasurement !== null && liveMeasurement !== undefined) {
      return formatMeasurement(liveMeasurement, mode === 'area');
    }
    
    // Show last completed measurement
    if (measureResults.length > 0) {
      const lastResult = measureResults[measureResults.length - 1];
      if (lastResult.distance) {
        return formatMeasurement(lastResult.distance, false);
      }
      if (lastResult.area) {
        return formatMeasurement(lastResult.area, true);
      }
    }
    
    return `0.00 ${mode === 'area' ? 'km²' : 'km'}`;
  };

  return (
    <Draggable
      handle=".measure-drag-handle"
      nodeRef={dragHandleRef}
      cancel=".measure-no-drag"
      bounds="body"
      defaultPosition={{ x: 0, y: 0 }}
    >
      <Box
        ref={dragHandleRef}
        sx={{
          position: 'absolute',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: 'rgba(24, 29, 38, 0.97)',
          backdropFilter: 'blur(15px)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          padding: '12px 16px',
          minWidth: '480px',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        {/* Drag Handle - Entire area is draggable */}
        <Box
          className="measure-drag-handle"
          sx={{
            cursor: 'move',
            display: 'flex',
            alignItems: 'center',
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.9)'
            }
          }}
        >
          <DragIcon fontSize="small" />
        </Box>

        {/* Mode Selection Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          background: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: '8px',
          padding: '4px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Tooltip title="Single Line Distance">
            <IconButton
              size="small"
              onClick={() => handleModeChange('line')}
              className="measure-no-drag"
              sx={{
                color: mode === 'line' ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                backgroundColor: mode === 'line' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                borderRadius: '6px',
                minWidth: '36px',
                height: '36px',
                '&:hover': {
                  backgroundColor: mode === 'line' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <LineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Polyline Distance">
            <IconButton
              size="small"
              onClick={() => handleModeChange('polyline')}
              className="measure-no-drag"
              sx={{
                color: mode === 'polyline' ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                backgroundColor: mode === 'polyline' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                borderRadius: '6px',
                minWidth: '36px',
                height: '36px',
                '&:hover': {
                  backgroundColor: mode === 'polyline' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <LineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Polygon Area">
            <IconButton
              size="small"
              onClick={() => handleModeChange('area')}
              className="measure-no-drag"
              sx={{
                color: mode === 'area' ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                backgroundColor: mode === 'area' ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                borderRadius: '6px',
                minWidth: '36px',
                height: '36px',
                '&:hover': {
                  backgroundColor: mode === 'area' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <AreaIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear All">
            <IconButton
              size="small"
              onClick={handleClear}
              className="measure-no-drag"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '6px',
                minWidth: '36px',
                height: '36px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Distance/Area Display with Clickable Unit */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
            {mode === 'area' ? 'Surface :' : 'Distance:'}
          </Typography>
          <Box 
            onClick={handleUnitToggle}
            sx={{
              background: isDrawing && distanceHasStarted && liveMeasurement !== null ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: isDrawing && distanceHasStarted && liveMeasurement !== null ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              padding: '6px 12px',
              minWidth: '80px',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isDrawing && distanceHasStarted && liveMeasurement !== null ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                borderColor: isDrawing && distanceHasStarted && liveMeasurement !== null ? 'rgba(76, 175, 80, 0.4)' : 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <Typography variant="caption" sx={{ 
              color: isDrawing && distanceHasStarted && liveMeasurement !== null ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              {getCurrentMeasurement()}
            </Typography>
          </Box>
        </Box>

        {/* Close Button */}
        <IconButton
          size="small"
          onClick={onClose}
          className="measure-no-drag"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: '#fff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Draggable>
  );
};

export default MeasureWidget;
